'use client';

import { useEffect, useRef } from 'react';
import posthog from 'posthog-js';
import { useAppData } from '@/lib/data-context';

export default function PostHogProvider() {
  const { user, userLoading } = useAppData();
  const initialized = useRef(false);
  const identifiedUserId = useRef<string | null>(null);

  // Init once, after auth resolves and a real user is present
  useEffect(() => {
    if (initialized.current) return;
    if (userLoading) return;
    if (!user?.id) return;

    const key = process.env.NEXT_PUBLIC_POSTHOG_KEY_REHAUT;
    const host = process.env.NEXT_PUBLIC_POSTHOG_HOST_REHAUT || 'https://us.i.posthog.com';
    if (!key) return;

    posthog.init(key, {
      api_host: host,
      capture_pageview: 'history_change',
      capture_pageleave: true,
      autocapture: true,
      // Rehaut runs no PostHog surveys; without this the SDK lazy-loads its
      // ~100KB surveys bundle on every page for nothing.
      disable_surveys: true,
      persistence: 'localStorage+cookie',
      capture_exceptions: true,
      session_recording: {
        maskAllInputs: true,
        maskTextSelector: '*',
        maskTextFn: (text, element) => {
          // Fail closed: if no element context, mask
          if (!element) return '*'.repeat(text.length);
          // Unmask if the text node is inside a <button type="button"> or
          // anything explicitly tagged data-ph-unmask
          if (element.closest('button, [data-ph-unmask]')) {
            return text;
          }
          // Default: mask
          return '*'.repeat(text.length);
        },
        // Belt-and-suspenders — block any element explicitly tagged
        blockSelector: '[data-ph-block]',
        recordHeaders: false,
        recordBody: false,
        // Even with recordBody/Headers off, defensively strip everything.
        // Rehaut /api/* routes return patient JSON.
        maskNetworkRequestFn: (request) => ({
          ...request,
          requestBody: null,
          responseBody: null,
          requestHeaders: undefined,
          responseHeaders: undefined,
        }),
      },
      loaded: (ph) => {
        if (process.env.NODE_ENV === 'development') {
          ph.debug();
        }
      },
    });

    initialized.current = true;
  }, [user?.id, userLoading]);

  // Identify the user once init has completed
  useEffect(() => {
    if (!initialized.current) return;
    if (!user?.id) return;
    if (identifiedUserId.current === user.id) return;

    posthog.identify(user.id, {
      // Safe properties only — role and system_role, not name
      role: user.role ?? 'unknown',
      system_role: user.system_role ?? 'unknown',
      is_admin: !!user.isAdmin,
      staff: true,
    });
    identifiedUserId.current = user.id;
  }, [user?.id, user?.role, user?.system_role, user?.isAdmin]);

  // Reset on logout so the next user starts fresh
  useEffect(() => {
    if (!initialized.current) return;
    if (userLoading) return;
    if (user?.id) return;
    if (!identifiedUserId.current) return;

    posthog.reset();
    identifiedUserId.current = null;
  }, [user?.id, userLoading]);

  return null;
}
